import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../../components/Button";
import { api } from "../../api/client";
import { MeetingNoteForm } from "./MeetingNoteForm";
import type { MeetingNote } from "../../api/types";

// 寫 / 改一份會議紀錄 —— **一整頁**(業主 2026-09-17)。
//
// > 優化系統會議記錄 新增不要用懸浮視窗 因為可能會議可能會很長
// > (不是只有會議紀錄/結案/檢討都要)
//
// ==========================================================================
// 為什麼懸浮視窗不行
// ==========================================================================
// 一份會議紀錄可能是三頁字。塞在視窗裡的下場有兩個,而且兩個都很煩:
//
// 1. 內容框只有十行高,寫的人在一個小框裡捲上捲下。
// 2. **背景整頁被遮住** —— 想回頭看一眼上一次的紀錄寫了什麼都做不到,
//    得先關掉、看完、再開一次重打。
//
// 整頁兩個問題都沒有:內容框給到 24 行,而且瀏覽器的上一頁就回得去。
//
// ==========================================================================
// 網址就是那一篇
// ==========================================================================
// `/meetings/12` 直接開得到第 12 篇。助理現在會回
// 「[會議紀錄|胸腔年會 場勘](/meetings/12)」,那個連結要真的到得了。
// `/meetings/new` 是新增,`?quote=7` 就掛在第 7 張單底下。

/** 內容框幾行高。**會議紀錄就是很長** —— 見檔頭。 */
const ROWS = 24;

export function MeetingNotePage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const [note, setNote] = useState<MeetingNote | undefined>();
  const [failed, setFailed] = useState("");

  useEffect(() => {
    if (isNew) return;
    let alive = true;
    void api
      .get<MeetingNote>(`/meeting-notes/${id}`)
      .then((got) => {
        if (alive) setNote(got);
      })
      .catch((err: unknown) => {
        // 找不到就說找不到。**空白一頁比錯誤訊息糟** ——
        // 他會以為是自己開錯,然後再點一次。
        if (alive) setFailed(err instanceof Error ? err.message : "打不開這一篇");
      });
    return () => {
      alive = false;
    };
  }, [id, isNew]);

  const quoteId = params.get("quote") ? Number(params.get("quote")) : null;

  if (failed) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
        <Back />
        <p className="m-0 text-sm text-[var(--danger)]">{failed}</p>
      </div>
    );
  }

  // 改一篇的時候要等它載進來才畫表單 —— 先畫空的再填,使用者會看到
  // 自己的字被「清空一次」,而那看起來像剛剛打的東西不見了。
  if (!isNew && !note) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
        <Back />
        <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
      <Back />
      <MeetingNoteForm
        note={note}
        quoteId={quoteId}
        rows={ROWS}
        onDone={() => navigate("/meetings")}
      >
        {({ save, saving, ready, editing }) => (
          // 按鈕放在**上面**:內容框二十幾行高,放在最底下的話
          // 寫完一篇要捲到天邊才按得到儲存。
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="m-0 text-xl font-semibold text-[var(--text)]">
              {editing ? "修改紀錄" : "寫一份紀錄"}
            </h2>
            <div className="flex items-center gap-2">
              <Button onClick={() => navigate("/meetings")}>取消</Button>
              <Button
                variant="primary"
                loading={saving}
                /* 標題與日期是唯二非有不可的。沒有標題的紀錄在列表上
                   分不出是哪一場會。 */
                disabled={!ready}
                onClick={save}
              >
                {editing ? "確定更新" : "確定建立"}
              </Button>
            </div>
          </div>
        )}
      </MeetingNoteForm>

      {/* **唯讀的索引,不是編輯控制項。**
          業主 2026-09-19 嫌舊的連結清單「根本只是HTAG」—— 嫌的是那是一個
          與內文無關、要另外維護的東西。這一份是從內文的提及推導出來的:
          它不可能與文章分岔,而寫一篇很長的紀錄時,一眼掃過「這篇碰到哪些
          東西」仍然有用。 */}
      {note && note.links.length > 0 ? (
        <ul aria-label="已連結" className="m-0 flex flex-wrap gap-2 p-0">
          {note.links.map((link) => (
            <li
              key={link.id}
              className="list-none rounded-full border border-[var(--border)] px-2.5 py-0.5 text-xs text-[var(--text-2)]"
            >
              {`${link.target_type_label}:${link.target_label}`}
              {/* **連結留著,只是點不進去。** 那場會議真的開過。 */}
              {link.is_dangling ? (
                <span className="ml-1 text-[var(--text-3)]">(已刪除)</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function Back() {
  return (
    <Link
      to="/meetings"
      className="inline-flex w-fit items-center gap-1 text-sm text-[var(--text-3)] no-underline hover:text-[var(--text)]"
    >
      <ArrowLeft aria-hidden="true" size={14} />
      回會議紀錄
    </Link>
  );
}
